import { useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { Plus } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Note } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';

import { NoteCard } from '../components/NoteCard';
import TransportProNoteCreatorDialog from '../components/TransportProNoteCreatorDialog';
import { TransportProFormLoad, TransportProNote } from '../types';
import {
  getTransportProIsPriorityNote,
  getTransportProNoteText,
} from '../utils';

type NotesSectionProps = {
  formMethods: UseFormReturn<TransportProFormLoad>;
  loadId: MaybeUndef<number>;
};

export function NotesSection({ formMethods, loadId }: NotesSectionProps) {
  const [isNoteCreatorOpen, setIsNoteCreatorOpen] = useState(false);
  const notes = (formMethods.watch('notes') ?? []) as TransportProNote[];

  const displayNotes = useMemo(() => {
    if (!Array.isArray(notes)) {
      return [];
    }

    return notes.filter(
      (note) => !note.isDeleted && getTransportProNoteText(note).length > 0
    );
  }, [notes]);

  const handleNoteCreated = (updatedNotes: Note[]) => {
    formMethods.setValue('notes', updatedNotes, { shouldDirty: false });
  };

  return (
    <Flex direction='col' gap='md' className='w-full'>
      {loadId != null ? (
        <>
          <Button
            buttonNamePosthog={null}
            type='button'
            variant='outline'
            className='w-full gap-2'
            onClick={() => setIsNoteCreatorOpen(true)}
            logProperties={{ loadID: loadId }}
          >
            <Plus className='h-4 w-4' />
            Add note
          </Button>

          <TransportProNoteCreatorDialog
            isOpen={isNoteCreatorOpen}
            onClose={() => setIsNoteCreatorOpen(false)}
            loadId={loadId}
            onNoteCreated={handleNoteCreated}
          />
        </>
      ) : null}

      {displayNotes.length === 0 ? (
        <Typography variant='body-sm' className='text-neutral-500'>
          No notes on this load yet.
        </Typography>
      ) : (
        <Flex direction='col' gap='md' className='w-full'>
          {displayNotes.map((note, index) => {
            const text = getTransportProNoteText(note);
            const isPriority = getTransportProIsPriorityNote(note);
            const key =
              note.noteId ??
              note.externalTMSID ??
              `note-${index}-${text.slice(0, 20)}`;

            return (
              <NoteCard
                key={key}
                index={index}
                text={text}
                isPriority={isPriority}
                cardKey={String(key)}
              />
            );
          })}
        </Flex>
      )}
    </Flex>
  );
}
